import {QuestionType} from "@reside/forms";
import {ErrorResponseDto} from "@reside/reside-api-admission";
import {
  Admission,
  AdmissionDocumentControllerApi,
  AdmissionNotificationModel,
  AdmissionFlag,
  PatchAdmissionDto,
  ChecklistItem,
  AdmissionNote,
} from "@reside/reside-api-admission";
import portableFetch from "portable-fetch";
import {ApiErrorData, http} from "./api";
import {Override} from "../utils/helpers/tsUtils";
import {BASE_PATH} from "../config";
import {getAuthorizationHeader} from "../models/admin-session/selectors";
import {getCommonAuthorizationHeader} from "./api/selectors";
import {http as httpApiGateway} from "./api/restApiClient";
import {withRetryOn504} from "./api/retry";
import {withAccessToken} from "../utils/url";
import {DocumentType} from "../utils/enums";
export const AdmissionStatus = Admission.StatusEnum;
export type AdmissionStatusEnum = keyof typeof AdmissionStatus;

export enum AdmissionRecordType {
  STAR = "STAR",
}

export enum AdmissionRecordAction {
  SET = "set",
  UNSET = "unset",
}

export type AdmissionRecordTypeRequest = {
  admission_id: string;
  record_type: AdmissionRecordType;
  action: AdmissionRecordAction;
};

export type AdmissionAnswerObject<Value = string | string[] | any> = {
  created?: number;
  fileReference?: boolean;
  hidden?: boolean;
  metadata?: any;
  preFlight?: boolean;
  questionId?: string;
  questionType: QuestionType;
  value: Value;
};

export type SignatureMetadata = {
  admission_id: string;
  signature: string;
  metadata: {
    stroke_data: any[];
    first_name: string;
    last_name: string;
  };
  action: string;
};

export type AdmissionAnswerObjects = Record<string, AdmissionAnswerObject>;

export const admissionsApi = new AdmissionDocumentControllerApi(
  undefined,
  BASE_PATH,
  portableFetch,
);

export const AdmissionsService = {
  get: (id: string) => http.get<Admission>(`/v3/admissions/${id}`),

  patch: (
    admissionId: string,
    admission: PatchAdmissionDto & {
      // When signing with scroll enforcement feature flag, we send these two fields for audit purposes.
      pdfPreviewLoadedTimestamp?: string;
      signatureConfirmedTimestamp?: string;
    },
  ) => http.patch<Admission>(`/v3/admissions/${admissionId}`, admission),

  patchAfterAdmissionAnswers: (
    admissionId: string,
    admission: PatchAdmissionDto,
  ) =>
    http.patch<Admission>(
      `/v3/admissions/${admissionId}/after-admission`,
      admission,
    ),

  patchNotes: (admissionId: string, admissionNotesData: AdmissionNote[]) =>
    admissionsApi.updateAdmissionNoteUsingPATCH(
      admissionNotesData,
      admissionId,
      getAuthorizationHeader(),
    ),

  preview: (admissionId: string) =>
    admissionsApi.getPreviewUsingGET(admissionId, getAuthorizationHeader()),

  create: (data: Admission) => http.post<Admission>("/v3/admissions", data),

  update: (id: string, data: Admission) =>
    http.put<Admission>(`/v3/admissions/${id}`, data),

  restore: (id: string) =>
    admissionsApi.restoreAdmissionUsingPATCH(id, getAuthorizationHeader()),

  transitions: (id: string) =>
    admissionsApi.getAdmissionTransitionsUsingGET(id, getAuthorizationHeader()),

  setInProgress: (admissionId: string) =>
    admissionsApi.sendAdmissionToResidentUsingPATCH(
      admissionId,
      getAuthorizationHeader(),
    ),

  delete: (admissionId: string, reason: string) =>
    admissionsApi.deleteAdmissionUsingPATCH(
      {reason},
      admissionId,
      getAuthorizationHeader(),
    ),

  sign: (admissionId: string) =>
    admissionsApi.signAdmissionUsingPATCH(
      admissionId,
      getAuthorizationHeader(),
    ),

  approve: (admissionId: string, data: any) =>
    admissionsApi.approveAdmissionUsingPATCH(
      data,
      admissionId,
      getAuthorizationHeader(),
    ),

  archive: (admissionId: string) =>
    admissionsApi.archiveAdmissionUsingPATCH(
      admissionId,
      getAuthorizationHeader(),
    ),

  patchAdmissionChecklistItem: ({
    admissionId,
    checklistItemId,
    data,
  }: {
    admissionId: string;
    checklistItemId: string;
    data: ChecklistItem;
  }) =>
    admissionsApi.patchChecklistItemUsingPATCH(
      data,
      admissionId,
      checklistItemId,
      getAuthorizationHeader(),
    ),

  sendNotification: (admissionId: string, data: AdmissionNotificationModel) =>
    // TODO: stopped using `admissionsApi.sendNotificationUsingPOST`. But it had tons of logic to handle the request.
    httpApiGateway.post<any>(`/admissions/${admissionId}/notification`, data, {
      headers: {
        Authorization: getCommonAuthorizationHeader(),
      },
    }),

  search: (query: string) =>
    admissionsApi.searchAdmissionsByQueryUsingGET(
      query,
      getAuthorizationHeader(),
    ),

  getFlags: (admissionId: string) =>
    admissionsApi.getAdmissionFlagsUsingGET(
      admissionId,
      getAuthorizationHeader(),
    ),

  deleteFlag: (admissionId: string, flagId: string) =>
    admissionsApi.deleteAdmissionFlagUsingDELETE(
      admissionId,
      flagId,
      getCommonAuthorizationHeader(),
    ),

  createFlag: (admissionId: string, flag: AdmissionFlag) =>
    admissionsApi.createAdmissionFlagUsingPOST(
      flag,
      admissionId,
      getCommonAuthorizationHeader(),
    ),

  saveSignatureMetadata: (admissionId: string, data: SignatureMetadata) =>
    httpApiGateway.post<any>(`/admissions/${admissionId}/signature`, data, {
      headers: {
        Authorization: getCommonAuthorizationHeader(),
      },
    }),

  forceDelete: (admissionId: string, reason: string) => {
    return http.patch<Admission>(`/v3/admissions/${admissionId}/force-delete`, {
      reason,
    });
  },

  starAdmission: (admissionId: string) =>
    httpApiGateway.post<any>("/admissions/admission-type", {
      admission_id: admissionId,
      record_type: AdmissionRecordType.STAR,
      action: AdmissionRecordAction.SET,
    } as AdmissionRecordTypeRequest),

  unstarAdmission: (admissionId: string) =>
    httpApiGateway.post<any>("/admissions/admission-type", {
      admission_id: admissionId,
      record_type: AdmissionRecordType.STAR,
      action: AdmissionRecordAction.UNSET,
    } as AdmissionRecordTypeRequest),
};

export type AdmissionPdfType = "full" | "arbitration" | "partial";

export type DownloadAdmissionPdfArgs = {
  admissionId: string;
  type?: AdmissionPdfType;
  group?: string;
  status?: string;
};

/**
 * Download an admission PDF (full, arbitration, or partial).
 *
 * For admissions with pre-generated PDFs (SIGNED, APPROVED, ARCHIVED, CANCELLED):
 * Fetches the presigned S3 URL from the download endpoint.
 *
 * For admissions without pre-generated PDFs (IN_PROGRESS, PRE_FLIGHT):
 * Calls the on-demand generation endpoint which generates the PDF in the Lambda,
 * uploads to S3, and returns a presigned URL.
 *
 * For partial: Uses the download endpoint (partials are always pre-generated).
 */
const PRE_GENERATED_STATUSES = new Set([
  "SIGNED",
  "APPROVED",
  "ARCHIVED",
  "CANCELLED",
]);

export const downloadAdmissionPdf = async ({
  admissionId,
  type = "full",
  group,
  status,
}: DownloadAdmissionPdfArgs): Promise<void> => {
  const hasPreGeneratedPdf =
    type === "partial" || PRE_GENERATED_STATUSES.has(status ?? "");

  const url = hasPreGeneratedPdf
    ? `/pdf/admissions/${admissionId}`
    : `/pdf/admissions/${admissionId}/generate`;

  const params: Record<string, string> = {type};
  if (group) {
    params.group = group;
  }

  const response = hasPreGeneratedPdf
    ? await httpApiGateway.get(url, params)
    : await withRetryOn504(() => httpApiGateway.get(url, params));

  const data = response.data as {downloadUrl: string};

  if (!data.downloadUrl) {
    throw new Error("Download URL not found in response");
  }

  window.location.href = data.downloadUrl;
};

/**
 * Download a blank (unfilled) template PDF.
 * Generates the PDF in the Lambda, uploads to S3 temp, and opens the presigned URL.
 *
 * @param facilityId - The facility ID
 * @param templateName - The template name
 * @param type - "full" for complete blank PDF, "arbitration" for arbitration-only
 */
export const downloadBlankPdf = async (
  facilityId: string,
  templateName: string,
  type: "full" | "arbitration" = "full",
): Promise<void> => {
  const response = await httpApiGateway.get(
    `/pdf/facilities/${facilityId}/templates/${templateName}`,
    {type},
  );

  if (!response.ok) {
    throw new Error(`Failed to generate blank PDF: ${response.status}`);
  }

  const data = response.data as {downloadUrl: string};

  if (!data.downloadUrl) {
    throw new Error("Download URL not found in response");
  }

  window.location.href = data.downloadUrl;
};

/**
 * Export admission audit data as a zip file.
 * Downloads a zip file containing admission records, JSON data, audit trail, and README.
 *
 * @param admissionId - The admission database ID (UUID) to export
 */
export const exportAdmissionAuditData = async (
  admissionId: string,
): Promise<void> => {
  // Note: httpApiGateway uses apisauce, so signature is get(url, params, axiosConfig)
  // Authorization header is automatically added by asyncRequestTransform
  // Endpoint returns JSON with pre-signed S3 URL
  const response = await httpApiGateway.get(
    `/admissions/${admissionId}/export-audit`,
    {}, // Empty query params
  );

  if (!response.ok) {
    throw new Error(`Failed to export audit data: ${response.status}`);
  }

  // Response contains JSON with download_url pointing to S3 pre-signed URL
  const data = response.data as {downloadUrl: string};

  if (!data.downloadUrl) {
    throw new Error("Download URL not found in response");
  }

  // Open pre-signed URL in new tab to trigger download.
  // Content-Disposition: attachment on the presigned URL ensures browser downloads rather than displays.
  window.location.href = data.downloadUrl;
};

export type SignError = ApiErrorData &
  Readonly<{
    data: Readonly<{
      isValid: boolean;
      errors: ReadonlyArray<SlideValidationError>;
    }>;
  }>;

type SlideValidationError = Readonly<{
  slideId: string;
  errors: ReadonlyArray<ValidationError>;
}>;

type ValidationError = Readonly<{
  field: string;
  message: string;
}>;

export type PatchAdmissionChecklistItem =
  typeof AdmissionsService.patchAdmissionChecklistItem;

export type AdmissionFromSearch = Override<
  Pick<
    Admission,
    | "id"
    | "code"
    | "facilityId"
    | "answers"
    | "facilityTimeZoneId"
    | "checklist"
    | "completionPercentage"
    | "status"
    | "signatureDate"
    | "created"
    | "updated"
    | "flags"
    | "resources"
    | "dateOfAdmission"
    | "pcc"
    | "templateDocumentTypes"
  >,
  {
    status: AdmissionStatusEnum;
    checklist: ReadonlyArray<ChecklistItem>;
    packetSize: number;
    starred?: boolean;
  }
>;

/**
 * Checks if an admission has arbitration documents based on its templateDocumentTypes.
 * @param templateDocumentTypes List of document types from the admission
 * @returns true if the admission has arbitration documents
 */
export const hasArbitrationDocument = (
  templateDocumentTypes: ReadonlyArray<string> | undefined,
): boolean => {
  return templateDocumentTypes?.includes(DocumentType.ARBITRATION) === true;
};

export const isMissingMandatoryChecklistItemException = (
  error: ErrorResponseDto,
) => error.exception.includes("MissingMandatoryChecklistItemException");
